import { useState, useEffect } from "react";
import { DOCUMENT_DROPZONE_ACCEPT } from "shared/constants/officeFiles";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";
import CustomModal from "shared/components/common/customModal";
import CustomPageHeading from "shared/components/common/customPageHeading";
import ImageItemsList, {
  ImageItem,
} from "shared/components/common/imageItemsList";

interface AddInspirationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (items: ImageItem[]) => void;
  existingItems?: ImageItem[];
  isSubmitting?: boolean;
}

const emptyItem: ImageItem = {
  id: "new-1",
  imageLabel: "",
  notes: "",
  hasImage: false,
};

const AddInspirationModal = ({
  isOpen,
  onClose,
  onSubmit,
  existingItems = [],
  isSubmitting = false,
}: AddInspirationModalProps) => {
  const [items, setItems] = useState<ImageItem[]>([emptyItem]);

  useEffect(() => {
    if (isOpen) {
      if (existingItems.length > 0) {
        const restored = existingItems.map((item) => {
          if (item.file instanceof File) {
            const freshUrl = URL.createObjectURL(item.file);
            return { ...item, imageUrl: freshUrl, hasImage: true };
          }
          return { ...item };
        });
        setItems(restored);
      } else {
        setItems([{ ...emptyItem, id: `new-${Date.now()}` }]);
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    const validItems = items.filter(
      (item) => item.hasImage && (item.file || item.imageUrl),
    );
    onSubmit(validItems);
  };

  const hasValidItems = items.some(
    (item) => item.hasImage && (item.file || item.imageUrl),
  );

  return (
    <CustomModal
      size="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Inspiration Images"
      description="Upload reference files with labels and notes."
      icon={Icons.CameraPlus}
      showBackButton={false}
      customContentContainer="noSidePadding"
      actionButtonText={isSubmitting ? "Adding..." : "Add Inspirations"}
      actionButtonIcon={Icons.BlueFileUpload}
      showModalHeader
      showModalFooter
      onNext={handleSubmit}
      actionButtonLoading={isSubmitting}
      actionButtonDisabled={isSubmitting || !hasValidItems}
    >
      <div className={classNames(styles.modalContentContainer)}>
        <CustomPageHeading
          title="Attach reference files or sketches"
          size="large"
        />

        <div className={styles.imageItemsListWrapper}>
          <ImageItemsList
            imageItems={items}
            onFileChange={(id, file, preview) => {
              setItems((prev) =>
                prev.map((item) =>
                  item.id === id
                    ? {
                        ...item,
                        file: file ?? null,
                        hasImage: !!(file || preview || item.imageUrl),
                        imageUrl: preview || item.imageUrl,
                      }
                    : item,
                ),
              );
            }}
            onImageLabelChange={(id, value) => {
              setItems((prev) =>
                prev.map((item) =>
                  item.id === id ? { ...item, imageLabel: value } : item,
                ),
              );
            }}
            onNotesChange={(id, value) => {
              setItems((prev) =>
                prev.map((item) =>
                  item.id === id ? { ...item, notes: value } : item,
                ),
              );
            }}
            onAddMore={() => {
              setItems((prev) => [
                ...prev,
                {
                  id: `new-${Date.now()}`,
                  imageLabel: "",
                  notes: "",
                  hasImage: false,
                },
              ]);
            }}
            onDelete={(id) => {
              setItems((prev) => {
                const next = prev.filter((item) => item.id !== id);
                return next.length > 0
                  ? next
                  : [
                      {
                        id: "new-1",
                        imageLabel: "",
                        notes: "",
                        hasImage: false,
                      },
                    ];
              });
            }}
            notesLabel="Additional Notes"
            showShadow={false}
            showPadding={false}
            accept={DOCUMENT_DROPZONE_ACCEPT}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default AddInspirationModal;
